import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Check from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Acent from 'components/Acent';
import ModalMenu from 'components/Modals';
import AppTextField from 'components/UI/inputs/AppTextField';
import React, { useEffect, useMemo, useRef } from 'react';
import { filter, find, get, map } from 'lodash';
import { useActivities } from 'store/activities/selectors';
import { useAllBook } from 'store/books/selectors';
import { useBookOrder } from 'store/books/selectors';
import { useBookUnits } from 'store/booksUnits/selectors';
import { useOrgStandardCodes } from 'store/organizations/selectors';
import { useResources } from 'store/resources/selectors';
import { useApExams } from 'store/apexams/selectors';
import { useRubrices } from 'store/rubric/selectors';
import { useStory } from 'store/story/selectors';
import useIsExamActive from 'components/hooks/books/useIsExamActive';
import { compareGradesForIllinoisCommonCore, normalizeStateForComparison, statesMatch } from 'utils/lib/standardHelper';
import { useStandards } from 'store/story/selectors';
import { ebookConfigDinamic, isVideo, mapBookByOrderOnlyCreateUser } from 'utils/lib/ebookHelper';

const EXAM_LABELS_FULL = [
  'Reading Only',
  'Reading and Listening',
  'Listening Only',
  'Email Response',
  'Essay (Argumentative)',
  'Simulated Conversation',
  'Cultural Comparison',
];
const EXAM_TYPE_LABEL = ['Reading', 'Speaking', 'Listening', 'Writing'];
const BOOK_CONTENT_REF = [
  { title: 'No content', value: 'no_content' },
  { title: 'Book AI content', value: 'book_ai_content' },
  { title: 'Book page images', value: 'book_page_images' },
];

type BookPage = { pageId?: string | number; NumberMasked?: string };

function filterOrdersByOrderIds(oneArray: { order?: (string | number)[] }[], orderIds: string | number) {
  const filteredOrders: { order?: (string | number)[] }[] = [];
  for (const orderItem of oneArray) {
    if (orderItem.order?.some((itemId) => orderIds === itemId)) {
      filteredOrders.push(orderItem);
    }
  }
  return filteredOrders;
}

function pageMatchesConfigId(page: BookPage | undefined, raw: unknown) {
  if (page == null || raw == null || raw === '') return false;
  const a = parseInt(String(page.pageId), 10);
  const b = parseInt(String(raw), 10);
  if (!Number.isNaN(a) && !Number.isNaN(b)) return a === b;
  return String(page.pageId) === String(raw);
}

function pageDisplayLabel(page: BookPage | undefined) {
  if (!page || typeof page !== 'object') return '';
  const masked = page.NumberMasked;
  if (masked != null && String(masked).trim() !== '') return String(masked);
  return page.pageId != null ? String(page.pageId) : '';
}

type CreteriaState = {
  sentenceStructure: boolean;
  grammar: boolean;
  orthography: boolean;
  sectionContent: boolean;
};

function hasAiGradingConfiguration(config: Record<string, unknown>): boolean {
  const ct = String(config.content_type || 'no_content');
  if (ct !== 'no_content') return true;
  if (config.from_page != null && String(config.from_page).trim() !== '') return true;
  if (config.to_page != null && String(config.to_page).trim() !== '') return true;
  const c = config.creteria as Partial<CreteriaState> | undefined;
  if (c && (c.sentenceStructure || c.grammar || c.orthography || c.sectionContent)) return true;
  if (String(config.teacher_instruction ?? '').trim() !== '') return true;
  return false;
}

const adminSettingsBoxSx = {
  flex: 1,
  minWidth: { xs: '100%', lg: 0 },
  p: 1.5,
  border: '1px solid #e2e8f0',
  borderRadius: 1,
  bgcolor: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
} as const;

function CheckCreteia({
  creteria,
  onCreteriaChange,
  showPageInput,
}: {
  creteria: Partial<CreteriaState>;
  onCreteriaChange: (next: CreteriaState) => void;
  showPageInput: boolean;
}) {
  const [selectedSections, setSelectedSections] = React.useState<CreteriaState>({
    sentenceStructure: false,
    grammar: false,
    orthography: false,
    sectionContent: false,
  });
  useEffect(() => {
    if (creteria && Object.keys(creteria).length > 0) {
      setSelectedSections((prev) => ({ ...prev, ...creteria }));
    }
  }, [creteria]);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = {
      ...selectedSections,
      [event.target.name]: event.target.checked,
    };
    setSelectedSections(next);
    onCreteriaChange(next);
  };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle2">Teacher AI Open-Ended Auto Grading</Typography>
      <FormGroup row sx={{ flexWrap: 'wrap', gap: 1.5 }}>
        <FormControlLabel
          control={<Checkbox checked={selectedSections.sentenceStructure} onChange={handleChange} name="sentenceStructure" color="primary" />}
          label="Sentence Structure"
        />
        <FormControlLabel
          control={<Checkbox checked={selectedSections.grammar} onChange={handleChange} name="grammar" color="primary" />}
          label="Grammar"
        />
        <FormControlLabel
          control={<Checkbox checked={selectedSections.orthography} onChange={handleChange} name="orthography" color="primary" />}
          label="Orthography"
        />
        {showPageInput ? (
          <FormControlLabel
            control={<Checkbox checked={selectedSections.sectionContent} onChange={handleChange} name="sectionContent" color="primary" />}
            label="Section Content"
          />
        ) : null}
      </FormGroup>
    </Box>
  );
}

function AiConfigurationBlock({
  config,
  onChangeConfig,
  pages,
}: {
  config: Record<string, unknown>;
  onChangeConfig: (next: Record<string, unknown>) => void;
  pages: BookPage[];
}) {
  const dispNameRef = useRef<HTMLInputElement | null>(null);
  const contentType = String(config.content_type || 'no_content');
  const showPageInput = contentType === 'book_ai_content' || contentType === 'book_page_images';
  const pageList = pages || [];
  const fromIndex =
    config.from_page != null && config.from_page !== ''
      ? pageList.findIndex((p) => pageMatchesConfigId(p, config.from_page))
      : -1;
  const toPageOptions = fromIndex >= 0 ? pageList.slice(fromIndex) : [];
  const toIndex =
    config.to_page != null && config.to_page !== ''
      ? pageList.findIndex((p) => pageMatchesConfigId(p, config.to_page))
      : -1;
  const fromPageValue = fromIndex >= 0 ? pageList[fromIndex] : null;
  const toPageValue = fromIndex >= 0 && toIndex >= fromIndex ? pageList[toIndex] : null;
  const creteria = (config.creteria || {}) as Partial<CreteriaState>;

  return (
    <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="builder-ai-content-type">Content Type</InputLabel>
            <Select
              labelId="builder-ai-content-type"
              value={contentType}
              label="Content Type"
              onChange={(e) =>
                onChangeConfig({
                  ...config,
                  content_type: e.target.value,
                  from_page: '',
                  to_page: '',
                })
              }
            >
              {BOOK_CONTENT_REF.map((v) => (
                <MenuItem key={v.value} value={v.value}>
                  {v.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {showPageInput ? (
            <>
              <Autocomplete
                options={pageList}
                getOptionLabel={(option) => pageDisplayLabel(option)}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return parseInt(String(option.pageId), 10) === parseInt(String(value.pageId), 10);
                }}
                value={fromPageValue}
                onChange={(_event, newValue) => {
                  onChangeConfig({
                    ...config,
                    from_page: newValue ? newValue.pageId : '',
                    to_page: '',
                  });
                }}
                renderInput={(params) => <TextField {...params} label="From Page" size="small" />}
              />
              <Autocomplete
                options={toPageOptions}
                getOptionLabel={(option) => pageDisplayLabel(option)}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return parseInt(String(option.pageId), 10) === parseInt(String(value.pageId), 10);
                }}
                value={toPageValue}
                disabled={fromIndex < 0}
                onChange={(_event, newValue) => {
                  onChangeConfig({
                    ...config,
                    to_page: newValue ? newValue.pageId : '',
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To Page"
                    size="small"
                    placeholder={fromIndex < 0 ? "Select 'From Page' first" : 'Select ending page'}
                  />
                )}
              />
            </>
          ) : null}
        </Stack>
        <CheckCreteia
          creteria={creteria}
          onCreteriaChange={(v) => onChangeConfig({ ...config, creteria: v })}
          showPageInput={showPageInput}
        />
        <Stack spacing={1}>
          <Acent inputRef={dispNameRef} setValue={(v) => onChangeConfig({ ...config, teacher_instruction: v })} style={{ flexShrink: 0 }} />
          <AppTextField
            label="Prompt for AI"
            multiline
            rows={2}
            inputRef={dispNameRef}
            value={String(config.teacher_instruction ?? '')}
            onChange={(e) => onChangeConfig({ ...config, teacher_instruction: e.target.value })}
          />
        </Stack>
      </Stack>
  );
}

export type SuperAdminActivityState = {
  level: string | null;
  storyType: boolean;
  apExamType: boolean;
  examCategories: string;
  assignmentStandards: string[];
  isExam: boolean;
  isUnitExam: boolean;
  unit: string | null;
  unit_exam: string | number | null;
  rubrics_id: string | null;
  ai_grading_config: Record<string, unknown>;
  pageLinkConfiguration: boolean;
};

function hasAdminActivityConfiguration(activity: SuperAdminActivityState): boolean {
  if (activity.level) return true;
  if (activity.isExam || activity.isUnitExam) return true;
  if (activity.rubrics_id) return true;
  if (Array.isArray(activity.assignmentStandards) && activity.assignmentStandards.length > 0) return true;
  if (activity.storyType || activity.apExamType) return true;
  if (String(activity.examCategories ?? '').trim() !== '') return true;
  return false;
}

type AssignmentBuilderSuperAdminPanelProps = {
  bookId: string | number;
  pageId: string | number;
  value: SuperAdminActivityState;
  onChange: (patch: Partial<SuperAdminActivityState>) => void;
  isFromEngagement?: boolean;
};

export function AssignmentBuilderSuperAdminPanel({
  bookId,
  pageId,
  value,
  onChange,
  isFromEngagement = false,
}: AssignmentBuilderSuperAdminPanelProps) {
  const standards = useStandards();
  const enabledStateCodes = useOrgStandardCodes();
  const allBooks = useAllBook();
  const activities = useActivities();
  const resources = useResources();
  const bookOrder = useBookOrder();
  const story = useStory();
  const exams = useApExams();
  const bookUnitList = useBookUnits();
  const AllRubricsData = useRubrices();
  const isVideoAvailable = useMemo(() => isVideo(String(bookId), resources), [bookId, resources]);
  const isExamActive = useIsExamActive({ bookId: String(bookId) });
  const checkExamBookType = Boolean(isExamActive?.checkExamBookType);

  const getBookData = useMemo(
    () => find(allBooks, (book: { id?: string | number }) => String(book?.id) === String(bookId)),
    [allBooks, bookId],
  );

  const { structures } = ebookConfigDinamic(bookId, allBooks);
  const orderedBook = mapBookByOrderOnlyCreateUser(allBooks, filterOrdersByOrderIds(bookOrder, bookId))[0]?.books || [];
  const filteredUnits =
    structures.find(
      (unit: { start?: unknown; finish?: unknown }) =>
        Number(pageId) >= parseInt(String(unit.start), 10) && Number(pageId) <= parseInt(String(unit.finish), 10),
    ) || {};

  const bookActivities = filter(activities, (ac) => String(ac.bookId) === String(bookId)) || [];
  const filteredActivities =
    bookActivities?.filter((activity) => {
      const pageNumber = parseInt(String(activity.page), 10);
      return (
        pageNumber >= parseInt(String(filteredUnits?.start), 10) &&
        pageNumber <= parseInt(String(filteredUnits?.finish), 10) &&
        activity.is_unit_exam === true
      );
    })?.length ?? 0;

  const isStoryBook = Boolean(find(story, ['bookId', bookId]));
  const isApExams = Boolean(find(exams, (k) => find(k.exams, (c) => String(c.bookId) === String(bookId))));
  const bookPages: BookPage[] = getBookData?.pages || [];

  const matchedReadLessonPage = useMemo(() => {
    const data = find(bookUnitList, ['bookId', bookId]);
    if (!data) return null;
    const pages = get(data, 'pages', []);
    const current = Number(pageId);
    if (Number.isNaN(current)) return null;
    return (
      find(pages, (p: { start?: unknown; finish?: unknown }) => {
        const start = Number(p.start);
        const finish = Number(p.finish);
        return current >= start && current <= finish;
      }) || null
    );
  }, [bookUnitList, bookId, pageId]);

  const readLessonPagePool = useMemo(() => {
    if (!matchedReadLessonPage) return bookPages;
    const lo = Number(matchedReadLessonPage.start);
    const hi = Number(matchedReadLessonPage.finish);
    const filtered = bookPages.filter((p) => {
      const n = parseInt(String(p.pageId), 10);
      return n >= lo && n <= hi;
    });
    return filtered.length > 0 ? filtered : bookPages;
  }, [bookPages, matchedReadLessonPage]);

  const defaultPageSelection = useMemo(() => {
    const current = parseInt(String(pageId), 10);
    const exists = bookPages.some((p) => parseInt(String(p.pageId), 10) === current);
    if (exists) return current;
    const first = bookPages[0]?.pageId;
    return first != null ? parseInt(String(first), 10) : 1;
  }, [pageId, bookPages]);

  const validPageSelectionValue = (val: unknown) => {
    const n = parseInt(String(val ?? defaultPageSelection), 10);
    return bookPages.some((p) => parseInt(String(p.pageId), 10) === n) ? n : defaultPageSelection;
  };

  const validReadLessonPageSelection = (val: unknown) => {
    const pool = readLessonPagePool;
    const fallback = parseInt(String(pool[0]?.pageId), 10);
    const fb = Number.isFinite(fallback) ? fallback : defaultPageSelection;
    const n = parseInt(String(val ?? defaultPageSelection), 10);
    return pool.some((p) => parseInt(String(p.pageId), 10) === n) ? n : fb;
  };

  const resolveReadLessonPageValue = (val: unknown) =>
    matchedReadLessonPage ? validReadLessonPageSelection(val) : validPageSelectionValue(val);

  const readLessonPageIdToIndex = useMemo(() => {
    const m = new Map<number, number>();
    readLessonPagePool.forEach((p, i) => {
      const id = parseInt(String(p.pageId), 10);
      if (!Number.isNaN(id) && !m.has(id)) m.set(id, i);
    });
    return m;
  }, [readLessonPagePool]);

  const effectivePageSelectionToValue = useMemo(() => {
    const fp = resolveReadLessonPageValue(value.ai_grading_config.page_selection_from);
    const tp = resolveReadLessonPageValue(value.ai_grading_config.page_selection_to);
    const fi = readLessonPageIdToIndex.get(fp) ?? 0;
    const ti = readLessonPageIdToIndex.get(tp) ?? 0;
    const p = readLessonPagePool[Math.max(fi, ti)];
    return p ? parseInt(String(p.pageId), 10) : fp;
  }, [
    value.ai_grading_config.page_selection_from,
    value.ai_grading_config.page_selection_to,
    readLessonPagePool,
    readLessonPageIdToIndex,
    matchedReadLessonPage,
    defaultPageSelection,
    bookPages,
  ]);

  useEffect(() => {
    if (filteredUnits?.id && orderedBook?.length > 0) {
      onChange({
        unit: filteredUnits?.shortName ?? null,
        unit_exam: filteredActivities + 1,
      });
    }
  }, [filteredUnits?.id, filteredActivities, orderedBook?.length]);

  const filteredStandardsOptions = useMemo(() => {
    const ebookGradeLevel = getBookData?.grade_level;
    let filteredByState = standards;
    if (enabledStateCodes && enabledStateCodes.length > 0) {
      filteredByState = filter(standards, (standard) => {
        const standardStatus = get(standard, 'status', false);
        if (standardStatus !== true) return false;
        const standardState = get(standard, 'state', '');
        const enabledStateNames = enabledStateCodes.map((code: string) => normalizeStateForComparison(code)).filter(Boolean);
        return enabledStateNames.some((enabledName: string) => statesMatch(standardState, enabledName));
      });
    } else {
      filteredByState = filter(standards, (standard) => get(standard, 'status', false) === true);
    }
    if (ebookGradeLevel) {
      const ebookGrades = ebookGradeLevel.split(',').map((g: string) => g.trim()).filter(Boolean);
      return map(
        filter(filteredByState, (standard) => {
          const standardStatus = get(standard, 'status', false);
          if (standardStatus !== true) return false;
          const standardGradeLevel = get(standard, 'grade_level', '');
          if (!standardGradeLevel) return true;
          const standardGrades = standardGradeLevel.split(',').map((g: string) => g.trim()).filter(Boolean);
          const standardState = String(get(standard, 'state', '')).toUpperCase();
          return ebookGrades.some((ebookGrade: string) =>
            standardGrades.some((standardGrade: string) =>
              compareGradesForIllinoisCommonCore(ebookGrade, standardGrade, standardState),
            ),
          );
        }),
        (k) => k.abbr,
      );
    }
    return map(
      filter(filteredByState, (standard) => get(standard, 'status', false) === true),
      (k) => k.abbr,
    );
  }, [standards, getBookData?.grade_level, enabledStateCodes]);

  const assignmentStandardsOptions = useMemo(
    () => filteredStandardsOptions.map((abbr) => ({ label: abbr, value: abbr })),
    [filteredStandardsOptions],
  );

  const showStandardsField = Boolean(enabledStateCodes && enabledStateCodes.length > 0);
  const selectedRubricData = useMemo(() => find(AllRubricsData, (k) => k.id === value.rubrics_id), [AllRubricsData, value.rubrics_id]);

  const onChangeAiConfig = (patch: Record<string, unknown>) => {
    onChange({ ai_grading_config: { ...value.ai_grading_config, ...patch } });
  };

  const aiGradingConfigured = useMemo(
    () => hasAiGradingConfiguration(value.ai_grading_config),
    [value.ai_grading_config],
  );

  const adminActivityConfigured = useMemo(
    () => hasAdminActivityConfiguration(value),
    [
      value.level,
      value.isExam,
      value.isUnitExam,
      value.rubrics_id,
      value.assignmentStandards,
      value.storyType,
      value.apExamType,
      value.examCategories,
    ],
  );

  const accordionShellSx = {
    border: '1px solid #e2e8f0',
    borderRadius: '8px !important',
    overflow: 'hidden',
    '&:before': { display: 'none' },
  } as const;

  return (
    <Stack spacing={2} sx={{ py: 2, borderTop: '1px solid #e2e8f0', mt: 1 }}>
      <Accordion defaultExpanded={false} disableGutters elevation={0} sx={accordionShellSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, bgcolor: '#f8fafc' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Admin activity options
            </Typography>
            {adminActivityConfigured ? <Check sx={{ color: 'success.main', fontSize: 22 }} aria-hidden /> : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pb: 2, pt: 1, bgcolor: '#fafafa' }}>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ width: '100%' }}>
              <Box sx={adminSettingsBoxSx}>
                <FormControl size="small" fullWidth>
                  <InputLabel
                    id="builder-sa-level"
                    shrink
                    sx={{
                      bgcolor: '#fafafa',
                      px: 0.5,
                    }}
                  >
                    Level
                  </InputLabel>
                  <Select
                    labelId="builder-sa-level"
                    label="Level"
                    notched
                    value={value.level || ''}
                    onChange={(e) => onChange({ level: e.target.value || null })}
                  >
                    <MenuItem value="BASICO">BASICO</MenuItem>
                    <MenuItem value="INTERMEDIO">INTERMEDIO</MenuItem>
                    <MenuItem value="AVANZADO">AVANZADO</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={adminSettingsBoxSx} style={{ alignItems: 'flex-start' }}>
                <FormControlLabel
                  sx={{ m: 0, alignItems: 'center' }}
                  control={
                    <Switch
                      size="small"
                      checked={value.isExam}
                      onChange={(e) => {
                        if (value.isUnitExam) {
                          onChange({ isExam: e.target.checked, isUnitExam: false, unit: null, unit_exam: null });
                        } else {
                          onChange({ isExam: e.target.checked });
                        }
                      }}
                    />
                  }
                  label="Mark as Quiz"
                />
              </Box>
              <Box sx={adminSettingsBoxSx} style={{ alignItems: 'flex-start' }}>
                <FormControlLabel
                  sx={{ m: 0, alignItems: 'center' }}
                  control={
                    <Switch
                      size="small"
                      checked={value.isUnitExam}
                      disabled={!filteredUnits?.id || orderedBook?.length === 0}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (value.isExam) {
                          onChange({ isUnitExam: checked, isExam: false });
                        } else {
                          onChange({
                            isUnitExam: checked,
                            ...(!checked ? { unit: null, unit_exam: null } : {}),
                          });
                        }
                      }}
                    />
                  }
                  label="Mark as Unit Exam"
                />
              </Box>
              <Box sx={{ ...adminSettingsBoxSx, minWidth: { xs: '100%', lg: 0 } }}>
                <ModalMenu
                  type="selectRubricModal"
                  is_placement={false}
                  addedRubric={selectedRubricData}
                  setAddedRubric={(val: { id?: string } | null) => onChange({ rubrics_id: val?.id ?? null })}
                  resetConfig={() => onChange({ rubrics_id: null })}
                />
              </Box>
            </Stack>
            {showStandardsField ? (
              <Box sx={{ ...adminSettingsBoxSx, width: '100%', minWidth: 0 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="builder-sa-standards">Standards</InputLabel>
                  <Select
                    labelId="builder-sa-standards"
                    multiple
                    label="Standards"
                    value={value.assignmentStandards || []}
                    onChange={(e) =>
                      onChange({ assignmentStandards: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value })
                    }
                    renderValue={(selected) => {
                      const s = selected as string[];
                      if (!s || s.length === 0) return '';
                      return s.length === 1 ? s[0] : `${s.length} standards selected`;
                    }}
                  >
                    {assignmentStandardsOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ) : null}
            {isStoryBook || isApExams ? (
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ width: '100%' }}>
                {isStoryBook ? (
                  <Box sx={{ ...adminSettingsBoxSx, flex: 1, minWidth: { xs: '100%', lg: 0 } }}>
                    <FormControlLabel
                      sx={{ m: 0, alignItems: 'flex-start' }}
                      control={
                        <Switch
                          size="small"
                          checked={value.storyType}
                          onChange={(e) => onChange({ storyType: e.target.checked })}
                        />
                      }
                      label="Assignment for Story"
                    />
                  </Box>
                ) : null}
                {isApExams ? (
                  <Box sx={{ ...adminSettingsBoxSx, flex: 1, minWidth: { xs: '100%', lg: 0 } }}>
                    <FormControlLabel
                      sx={{ m: 0, alignItems: 'flex-start' }}
                      control={
                        <Switch
                          size="small"
                          checked={value.apExamType}
                          onChange={(e) => onChange({ apExamType: e.target.checked })}
                        />
                      }
                      label="Assignment for AP"
                    />
                  </Box>
                ) : null}
              </Stack>
            ) : null}
            {isApExams || checkExamBookType ? (
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} sx={{ width: '100%' }}>
                {isApExams ? (
                  <Box sx={{ ...adminSettingsBoxSx, flex: 1, minWidth: { xs: '100%', lg: 0 } }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="builder-ap-cat">Category for AP Exams</InputLabel>
                      <Select
                        labelId="builder-ap-cat"
                        label="Category for AP Exams"
                        value={value.examCategories}
                        onChange={(e) => onChange({ examCategories: String(e.target.value) })}
                      >
                        {EXAM_LABELS_FULL.map((v) => (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : null}
                {checkExamBookType ? (
                  <Box sx={{ ...adminSettingsBoxSx, flex: 1, minWidth: { xs: '100%', lg: 0 } }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="builder-exam-cat">Category for Exams</InputLabel>
                      <Select
                        labelId="builder-exam-cat"
                        label="Category for Exams"
                        value={value.examCategories}
                        onChange={(e) => onChange({ examCategories: String(e.target.value) })}
                      >
                        {EXAM_TYPE_LABEL.map((v) => (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ) : null}
              </Stack>
            ) : null}
            {value.isUnitExam ? (
              <Box sx={{ ...adminSettingsBoxSx, width: '100%', minWidth: 0 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
                  <Typography variant="body2" sx={{ flex: '1 1 auto', minWidth: 0 }}>
                    {getBookData?.description} — {getBookData?.id}
                  </Typography>
                  <TextField size="small" label="Unit" value={value.unit || ''} disabled sx={{ flex: '0 1 140px' }} />
                  <TextField size="small" label="Unit Exam" value={String(value.unit_exam ?? '')} disabled sx={{ flex: '0 1 140px' }} />
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded={false} disableGutters elevation={0} sx={accordionShellSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, bgcolor: '#f8fafc' }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ pr: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              AI Grading Configuration
            </Typography>
            {aiGradingConfigured ? <Check sx={{ color: 'success.main', fontSize: 22 }} aria-hidden /> : null}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0, bgcolor: '#fafafa' }}>
          <AiConfigurationBlock
            config={value.ai_grading_config}
            onChangeConfig={(next) => onChange({ ai_grading_config: next })}
            pages={bookPages}
          />
        </AccordionDetails>
      </Accordion>
      {!isVideoAvailable && !isFromEngagement ? (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={value.pageLinkConfiguration}
              onChange={(e) => onChange({ pageLinkConfiguration: e.target.checked })}
            />
          }
          label="Read Lesson Configuration"
        />
      ) : null}
      {!isVideoAvailable && value.pageLinkConfiguration ? (
        <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
          <Typography fontWeight={600} sx={{ mb: 1 }}>
            Read Lesson page range
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="page-selection-from">From</InputLabel>
              <Select
                labelId="page-selection-from"
                value={resolveReadLessonPageValue(value.ai_grading_config.page_selection_from)}
                label="From"
                onChange={(e) => {
                  const newFrom = e.target.value;
                  const nfi = readLessonPageIdToIndex.get(parseInt(String(newFrom), 10)) ?? 0;
                  const currentToPid = resolveReadLessonPageValue(value.ai_grading_config.page_selection_to);
                  const currentToIdx = readLessonPageIdToIndex.get(currentToPid) ?? 0;
                  onChangeAiConfig({
                    page_selection_from: newFrom,
                    ...(currentToIdx < nfi && { page_selection_to: newFrom }),
                  });
                }}
              >
                {readLessonPagePool.map((p) => (
                  <MenuItem key={String(p.pageId)} value={parseInt(String(p.pageId), 10)}>
                    {p.NumberMasked || `Page ${p.pageId}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="page-selection-to">To</InputLabel>
              <Select
                labelId="page-selection-to"
                value={effectivePageSelectionToValue}
                label="To"
                onChange={(e) => onChangeAiConfig({ page_selection_to: e.target.value })}
              >
                {readLessonPagePool
                  .filter((_, idx) => {
                    const fromPid = resolveReadLessonPageValue(value.ai_grading_config.page_selection_from);
                    const fromIdx = readLessonPageIdToIndex.get(fromPid) ?? 0;
                    return idx >= fromIdx;
                  })
                  .map((p) => (
                    <MenuItem key={String(p.pageId)} value={parseInt(String(p.pageId), 10)}>
                      {p.NumberMasked || `Page ${p.pageId}`}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
}

export function mergeAiGradingConfigForPayload(
  config: Record<string, unknown>,
  pageLinkEnabled: boolean,
  defaultPageSelection: number,
  bookPages: BookPage[],
  matchedReadLessonPage: { start?: unknown; finish?: unknown } | null,
  readLessonPagePool: BookPage[],
) {
  if (!pageLinkEnabled) {
    const { page_selection_from, page_selection_to, ...rest } = config || {};
    return rest;
  }
  const c = config || {};
  const validPageSelectionValue = (val: unknown) => {
    const n = parseInt(String(val ?? defaultPageSelection), 10);
    return bookPages.some((p) => parseInt(String(p.pageId), 10) === n) ? n : defaultPageSelection;
  };
  const validReadLessonPageSelection = (val: unknown) => {
    const pool = readLessonPagePool;
    const fallback = parseInt(String(pool[0]?.pageId), 10);
    const fb = Number.isFinite(fallback) ? fallback : defaultPageSelection;
    const n = parseInt(String(val ?? defaultPageSelection), 10);
    return pool.some((p) => parseInt(String(p.pageId), 10) === n) ? n : fb;
  };
  const rawFrom = c.page_selection_from ?? defaultPageSelection;
  const rawTo = c.page_selection_to ?? defaultPageSelection;
  const fromSel = matchedReadLessonPage ? validReadLessonPageSelection(rawFrom) : validPageSelectionValue(rawFrom);
  const toSel = matchedReadLessonPage ? validReadLessonPageSelection(rawTo) : validPageSelectionValue(rawTo);
  return {
    ...c,
    page_selection_from: fromSel,
    page_selection_to: Math.max(fromSel, toSel),
  };
}
